import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import { useState } from "react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

function TagInput({ value = [], onChange, placeholder, testid }) {
  const [text, setText] = useState("");
  const arr = Array.isArray(value) ? value : [];
  const add = () => {
    const v = text.trim();
    if (v && !arr.includes(v)) onChange([...arr, v]);
    setText("");
  };
  return (
    <div>
      <div className="flex gap-2">
        <Input
          data-testid={testid}
          value={text}
          placeholder={placeholder || "Skriv och tryck Enter"}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); add(); }
          }}
        />
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        {arr.map((t, i) => (
          <Badge key={i} variant="secondary" className="rounded-full pl-3 pr-1 py-1 text-sm">
            {t}
            <button type="button" className="ml-1 hover:text-destructive" onClick={() => onChange(arr.filter((_, j) => j !== i))}>
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>
    </div>
  );
}

function Likert({ value, onChange, testid }) {
  const labels = ["Instämmer inte alls", "", "Neutral", "", "Instämmer helt"];
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          data-testid={`${testid}-${n}`}
          onClick={() => onChange(n)}
          className={`h-10 w-10 rounded-full border text-sm font-medium transition-colors ${
            value === n ? "bg-primary text-primary-foreground border-primary" : "bg-white hover:bg-muted border-border"
          }`}
          title={labels[n - 1]}
        >
          {n}
        </button>
      ))}
      <span className="text-xs text-muted-foreground ml-1">1 = inte alls · 5 = helt</span>
    </div>
  );
}

export default function FieldRenderer({ field, value, onChange }) {
  const id = `field-${field.name}`;
  const common = { "data-testid": id, id };
  return (
    <div className="space-y-2">
      {field.type !== "switch" && <Label htmlFor={id} className="text-sm font-medium">{field.label}</Label>}
      {field.type === "text" && (
        <Input {...common} value={value || ""} placeholder={field.placeholder} onChange={(e) => onChange(e.target.value)} />
      )}
      {field.type === "textarea" && (
        <Textarea {...common} value={value || ""} placeholder={field.placeholder} rows={field.rows || 3} onChange={(e) => onChange(e.target.value)} />
      )}
      {field.type === "select" && (
        <Select value={value || ""} onValueChange={onChange}>
          <SelectTrigger {...common}><SelectValue placeholder={field.placeholder || "Välj"} /></SelectTrigger>
          <SelectContent>
            {field.options.map((o) => (
              <SelectItem key={o} value={o}>{o}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {field.type === "switch" && (
        <div className="flex items-center justify-between rounded-lg border border-border p-3">
          <Label htmlFor={id} className="text-sm">{field.label}</Label>
          <Switch {...common} checked={!!value} onCheckedChange={onChange} />
        </div>
      )}
      {field.type === "tags" && (
        <TagInput testid={id} value={value} onChange={onChange} placeholder={field.placeholder} />
      )}
      {field.type === "multiselect" && (
        <div className="flex flex-wrap gap-2" data-testid={id}>
          {field.options.map((o) => {
            const val = typeof o === "string" ? o : o.value;
            const lbl = typeof o === "string" ? o : o.label;
            const arr = Array.isArray(value) ? value : [];
            const on = arr.includes(val);
            return (
              <button
                key={val}
                type="button"
                data-testid={`${id}-${val}`}
                onClick={() => onChange(on ? arr.filter((x) => x !== val) : [...arr, val])}
                className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                  on ? "bg-primary text-primary-foreground border-primary" : "bg-white hover:bg-muted border-border"
                }`}
              >
                {lbl}
              </button>
            );
          })}
        </div>
      )}
      {field.type === "likert" && (
        <Likert testid={id} value={value} onChange={onChange} />
      )}
      {field.type === "choice" && (
        <div className="grid gap-2" data-testid={id}>
          {field.options.map((o) => {
            const val = typeof o === "string" ? o : o.value;
            const lbl = typeof o === "string" ? o : o.label;
            const on = value === val;
            return (
              <button
                key={val}
                type="button"
                data-testid={`${id}-${val}`}
                onClick={() => onChange(val)}
                className={`text-left rounded-xl border px-4 py-3 text-sm transition-colors ${
                  on ? "bg-primary text-primary-foreground border-primary" : "bg-white hover:bg-muted border-border"
                }`}
              >
                {lbl}
              </button>
            );
          })}
        </div>
      )}
      {field.help && <p className="text-xs text-muted-foreground">{field.help}</p>}
    </div>
  );
}
